"use client";

import React, { useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import { getImageUrl } from "@/utils/utils";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface PageHeroProps {
  title: string;
  breadcrumbs: BreadcrumbItem[];
  backgroundImage?: {
    url?: string;
    alt?: string;
  };
  fallbackBackgroundImage?: string;
  className?: string;
  height?: "sm" | "md" | "lg";
  overlay?: boolean;
  overlayOpacity?: number;
}

const heightClasses = {
  sm: "h-[200px] sm:h-[250px] lg:h-[300px]",
  md: "h-[250px] sm:h-[300px] lg:h-[360px]",
  lg: "h-[300px] sm:h-[400px] lg:h-[500px]",
};

const sectionVariants = {
  hidden: {
    opacity: 0,
    y: 60,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: "circOut" as const,
    },
  },
};

export default function PageHero({
  title,
  breadcrumbs,
  backgroundImage,
  fallbackBackgroundImage = "/images/shared/empty-bg.jpg",
  className = "",
  height = "md",
  overlay = true,
  overlayOpacity = 50,
}: PageHeroProps) {
  useEffect(() => {
    const hash = window.location.hash;
    // Scroll to top when component mounts
    if (!hash || hash === "") {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <motion.div
      className={`relative ${heightClasses[height]} flex items-center justify-center ${className}`}
      initial="hidden"
      animate="visible"
      variants={sectionVariants}
    >
      {/* Fallback background image */}
      <Image
        src={fallbackBackgroundImage}
        alt="Page background"
        fill
        className="object-cover"
        priority
      />

      {/* Custom background image */}
      {backgroundImage?.url && (
        <div className="absolute inset-0">
          <Image
            src={getImageUrl(backgroundImage.url)}
            alt={backgroundImage.alt || "Page background"}
            fill
            className="object-cover"
            priority
          />
          {overlay && (
            <div
              className="absolute inset-0 bg-black"
              style={{ opacity: overlayOpacity / 100 }}
            />
          )}
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 text-center text-white max-w-[600px] px-4">
        <h1 className="text-2xl sm:text-3xl lg:text-[44px] font-medium leading-tight lg:leading-[58px] lg:tracking-[-1.44px] mb-4">
          {title}
        </h1>

        {/* Breadcrumbs */}
        <div className="flex items-center gap-2 justify-center text-sm sm:text-base lg:text-lg font-medium leading-[26px]">
          {breadcrumbs.map((breadcrumb, index) => (
            <React.Fragment key={index}>
              {breadcrumb.href ? (
                <Link
                  href={breadcrumb.href}
                  className={`text-white/90 hover:text-white transition-colors cursor-pointer`}
                >
                  {breadcrumb.label}
                </Link>
              ) : (
                <span className="text-white/90 text-left line-clamp-1">
                  {breadcrumb.label}
                </span>
              )}

              {index < breadcrumbs.length - 1 && (
                <svg
                  width="8"
                  height="14"
                  viewBox="0 0 8 14"
                  fill="none"
                  className="text-white w-2 h-3.5"
                >
                  <path
                    d="M6.91598 7.37484L2.03865 12.2106C1.83065 12.4187 1.49731 12.4187 1.33065 12.2106L1.03865 11.9184C0.989752 11.8735 0.950722 11.8188 0.924023 11.758C0.897324 11.6971 0.883539 11.6314 0.883539 11.5649C0.883539 11.4985 0.897324 11.4327 0.924023 11.3719C0.950722 11.311 0.989752 11.2564 1.03865 11.2114L5.29198 6.99998L1.03865 2.83123C0.989537 2.78624 0.950323 2.73152 0.923495 2.67054C0.896667 2.60957 0.882812 2.54367 0.882812 2.47705C0.882812 2.41043 0.896667 2.34454 0.923495 2.28357C0.950323 2.22259 0.989537 2.16787 1.03865 2.12288L1.33198 1.83073C1.49865 1.62263 1.83198 1.62263 2.03998 1.83073L6.95865 6.66648C7.04277 6.7679 7.08517 6.89754 7.07725 7.0291C7.06933 7.16065 7.01167 7.28426 6.91598 7.37484Z"
                    fill="white"
                  />
                </svg>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
